import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { X } from "lucide-react";

const LightboxContext = createContext({ open: () => {} });

export const useLightbox = () => useContext(LightboxContext);

export const LightboxProvider = ({ children }) => {
  const [img, setImg] = useState(null); // { src, alt }
  const open = useCallback((src, alt = "") => setImg({ src, alt }), []);
  const close = useCallback(() => setImg(null), []);

  useEffect(() => {
    if (!img) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden"; // lock scroll while open
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [img, close]);

  return (
    <LightboxContext.Provider value={{ open }}>
      {children}
      {img && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
          data-testid="lightbox"
          onClick={close}
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 sm:p-8 cursor-zoom-out animate-[fadeIn_0.15s_ease-out]"
        >
          <button
            type="button"
            aria-label="Close image"
            onClick={close}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/80 hover:text-white transition-colors"
          >
            <X size={32} strokeWidth={1.5} />
          </button>
          <img
            src={img.src}
            alt={img.alt}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[88vh] object-contain shadow-2xl cursor-default select-none"
          />
        </div>
      )}
    </LightboxContext.Provider>
  );
};
